import React, { useState, useCallback, useMemo } from 'react';
import { View, Text, Pressable, Platform } from 'react-native';
import { ChevronDown, ChevronUp } from 'lucide-react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { useTheme } from '../../../src/theme/useTheme';
import { getWcColors } from './matchPickStats.helper';
import { MATCH_PICK_STATS_COPY } from './MatchPickStats.constants';
import { getPickStatsOddsTableStyles } from './PickStatsOddsTable.styles';
import type { PickStatsOddsMarket } from '../../../src/worldcup/helpers/matchPickStatsApi';

const ALWAYS_VISIBLE_MARKET_IDS = new Set([1, 3]);
const EXPANDABLE_MARKET_IDS = new Set([5, 10]);
const CORRECT_SCORE_MARKET_ID = 10;

interface PickStatsOddsTableProps {
  odds: PickStatsOddsMarket[];
}

function parseScoreGoals(value: string): { home: number; away: number } | null {
  const parts = value.split(/[:\-–]/);
  if (parts.length === 2) {
    const home = parseInt(parts[0].trim(), 10);
    const away = parseInt(parts[1].trim(), 10);
    if (!isNaN(home) && !isNaN(away)) return { home, away };
  }
  return null;
}

function formatScoreLabel(value: string): string {
  const parsed = parseScoreGoals(value);
  if (parsed) return `${parsed.home}-${parsed.away}`;
  return value;
}

const CorrectScoreMarket: React.FC<{
  market: PickStatsOddsMarket;
  s: ReturnType<typeof getPickStatsOddsTableStyles>;
  purpleHi: string;
}> = ({ market, s, purpleHi }) => {
  const [showAll, setShowAll] = useState(false);

  const mainScores = useMemo(
    () => market.values.filter((v) => {
      const p = parseScoreGoals(v.value);
      return p != null && p.home <= 3 && p.away <= 3;
    }),
    [market.values],
  );

  const extraScores = useMemo(
    () => market.values.filter((v) => {
      const p = parseScoreGoals(v.value);
      return p == null || p.home > 3 || p.away > 3;
    }),
    [market.values],
  );

  const displayed = showAll ? [...mainScores, ...extraScores] : mainScores;

  return (
    <View style={s.marketCardColumn}>
      <Text style={s.marketName}>{market.name}</Text>
      <View style={s.scoreGrid}>
        {displayed.map((v) => (
          <View key={v.value} style={s.scoreChip}>
            <Text style={s.scoreChipScore}>{formatScoreLabel(v.value)}</Text>
            <Text style={s.scoreChipOdd}>{v.odd.toFixed(2)}</Text>
          </View>
        ))}
      </View>
      {extraScores.length > 0 ? (
        <Pressable
          accessibilityRole="button"
          onPress={() => setShowAll((p) => !p)}
          style={({ pressed }) => [
            s.moreBtn,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && s.moreBtnPressed,
          ]}
        >
          <Text style={s.moreBtnText}>
            {showAll ? MATCH_PICK_STATS_COPY.ODDS_LESS : `+${extraScores.length} more scores`}
          </Text>
          {showAll
            ? <ChevronUp size={10} color={purpleHi} />
            : <ChevronDown size={10} color={purpleHi} />}
        </Pressable>
      ) : null}
    </View>
  );
};

const MarketCard: React.FC<{ market: PickStatsOddsMarket; s: ReturnType<typeof getPickStatsOddsTableStyles> }> = ({ market, s }) => (
  <View style={s.marketCard}>
    <View style={s.marketLabelCol}>
      <Text style={s.marketName} numberOfLines={3}>{market.name}</Text>
    </View>
    <View style={s.chipsRow}>
      {market.values.map((v) => (
        <View key={v.value} style={s.chip}>
          <Text style={s.chipLabel} numberOfLines={2} adjustsFontSizeToFit minimumFontScale={0.7}>{v.value}</Text>
          <Text style={s.chipValue}>{v.odd.toFixed(2)}</Text>
        </View>
      ))}
    </View>
  </View>
);

export const PickStatsOddsTable: React.FC<PickStatsOddsTableProps> = ({ odds }) => {
  const theme = useTheme();
  const C = useMemo(() => getWcColors(theme), [theme]);
  const s = useMemo(() => getPickStatsOddsTableStyles(C), [C]);

  const [expanded, setExpanded] = useState(false);
  const toggle = useCallback(() => setExpanded((v) => !v), []);

  if (odds.length === 0) {
    return <Text style={s.emptyText}>{MATCH_PICK_STATS_COPY.NO_ODDS}</Text>;
  }

  const always = odds.filter((m) => ALWAYS_VISIBLE_MARKET_IDS.has(m.id));
  const expandable = odds.filter((m) => EXPANDABLE_MARKET_IDS.has(m.id));
  const correctScoreMarket = expandable.find((m) => m.id === CORRECT_SCORE_MARKET_ID);
  const otherExpandable = expandable.filter((m) => m.id !== CORRECT_SCORE_MARKET_ID);
  const visible = expanded ? [...always, ...otherExpandable] : always;

  return (
    <View style={s.container}>
      {visible.map((market) => (
        <MarketCard key={market.id} market={market} s={s} />
      ))}
      {expandable.length > 0 ? (
        <Pressable
          accessibilityRole="button"
          onPress={toggle}
          style={({ pressed }) => [
            s.moreBtn,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && s.moreBtnPressed,
          ]}
        >
          <Text style={s.moreBtnText}>
            {expanded ? MATCH_PICK_STATS_COPY.ODDS_LESS : MATCH_PICK_STATS_COPY.ODDS_MORE}
          </Text>
          {expanded
            ? <ChevronUp size={12} color={C.purpleHi} />
            : <ChevronDown size={12} color={C.purpleHi} />}
        </Pressable>
      ) : null}
      {expanded && correctScoreMarket ? (
        <CorrectScoreMarket market={correctScoreMarket} s={s} purpleHi={C.purpleHi} />
      ) : null}
    </View>
  );
};
